import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useGetBalance } from '../../api/queries';
import { STOCK_STATE_LABEL } from '../../utils/mapping';
import { formatDate } from '../../utils/formatters';
import { fr } from '../../i18n/fr';
import { useInventoryProducts } from '../../hooks/useInventoryProducts';
import { ProductSelectionItem } from '../../types';
import HistoryTable from './HistoryTable';
import type { InventoryBalance, StockState } from '../../api/dto';
import { formatPaginationLabel } from '../../utils/pagination';

type CorrectedSummaryKey = 'corrected_positive' | 'corrected_negative';
type SummaryStateKey = StockState | CorrectedSummaryKey;
interface StateSummary {
  key: SummaryStateKey;
  label: string;
  quantity: number;
}

interface GroupedBalanceRow {
  key: string;
  productId: string | null;
  product?: ProductSelectionItem;
  rows: InventoryBalance[];
  totalsByState: Partial<Record<SummaryStateKey, number>>;
  totalQuantity: number;
  lastMovementTs: string | null;
  lastMovementValue: number;
}

type DisplayedStockState = Exclude<StockState, 'recycled'>;

const STOCK_STATES_ORDER: DisplayedStockState[] = [
  'available',
  'reserved',
  'damaged',
  'lost',
  'sold',
  'corrected',
];

interface Props {
  storeId: string;
  stores: Array<{ id: string; name: string }>;
  onSelectProduct?: (product: ProductSelectionItem) => void;
  onPrintProduct?: (product: ProductSelectionItem) => void;
  selectedProductId?: string | null;
}

const BalanceTable: React.FC<Props> = ({
  storeId,
  stores,
  onSelectProduct,
  onPrintProduct,
  selectedProductId,
}) => {
  const balanceResult = useGetBalance({ store_id: storeId });
  const data = balanceResult?.data;
  const isPending = balanceResult?.isPending ?? false;
  const error = balanceResult?.error ?? null;
  const productsResult = useInventoryProducts();
  const products = productsResult?.data;
  const [stockStateFilter, setStockStateFilter] = React.useState<StockState | 'all'>(
    'all'
  );

  const availableStockTotal = React.useMemo(() => {
    if (!data?.length) {
      return 0;
    }

    return data.reduce((sum, row) => {
      if (row.store_id === storeId && row.stock_state === 'available') {
        return sum + row.quantity;
      }
      return sum;
    }, 0);
  }, [data, storeId]);

  const productMap = React.useMemo(() => {
    if (!products?.length) {
      return new Map<string, ProductSelectionItem>();
    }
    return new Map(products.map((product) => [product.id, product]));
  }, [products]);

  const storeMap = React.useMemo(
    () => new Map(stores.map((store) => [store.id, store.name])),
    [stores]
  );

  const storeRecord = React.useMemo(
    () => Object.fromEntries(stores.map((store) => [store.id, store.name])),
    [stores]
  );

  const selectedStoreName = storeMap.get(storeId) ?? '';

  const formattedAvailableTotal = React.useMemo(
    () => availableStockTotal.toLocaleString('fr-FR'),
    [availableStockTotal]
  );

  const groupedData = React.useMemo(() => {
    const source = data ?? [];
    if (!source.length) {
      return [] as GroupedBalanceRow[];
    }
    let unknownCounter = 0;
    const groups = new Map<string, GroupedBalanceRow>();
    const updateTotals = (
      totals: GroupedBalanceRow['totalsByState'],
      currentRow: InventoryBalance
    ) => {
      if (currentRow.stock_state === 'corrected') {
        const correctedKey: CorrectedSummaryKey =
          currentRow.quantity >= 0 ? 'corrected_positive' : 'corrected_negative';
        totals[correctedKey] =
          (totals[correctedKey] ?? 0) + Math.abs(currentRow.quantity);
      } else {
        const stateKey = currentRow.stock_state;
        totals[stateKey] = (totals[stateKey] ?? 0) + currentRow.quantity;
      }
    };

    source.forEach((row) => {
      if (row.stock_state === 'recycled') {
        return;
      }

      const key = row.product_id ?? `__unknown-${unknownCounter++}`;
      const product = row.product_id ? productMap.get(row.product_id) : undefined;
      const lastMovementValue = row.last_movement_ts
        ? new Date(row.last_movement_ts).getTime()
        : 0;
      const existing = groups.get(key);
      if (existing) {
        existing.rows.push(row);
        existing.totalQuantity += row.quantity;
        updateTotals(existing.totalsByState, row);
        if (lastMovementValue > existing.lastMovementValue) {
          existing.lastMovementValue = lastMovementValue;
          existing.lastMovementTs = row.last_movement_ts ?? null;
        }
        if (!existing.product && product) {
          existing.product = product;
        }
      } else {
        const totalsByState: GroupedBalanceRow['totalsByState'] = {};
        updateTotals(totalsByState, row);
        groups.set(key, {
          key,
          productId: row.product_id ?? null,
          product,
          rows: [row],
          totalsByState,
          totalQuantity: row.quantity,
          lastMovementTs: row.last_movement_ts ?? null,
          lastMovementValue: lastMovementValue,
        });
      }
    });
    const values = Array.from(groups.values());
    values.sort((a, b) => {
      const nameA =
        a.product?.name ?? a.product?.description ?? a.productId ?? '';
      const nameB =
        b.product?.name ?? b.product?.description ?? b.productId ?? '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
    return values;
  }, [data, productMap]);


  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [detailGroup, setDetailGroup] = React.useState<GroupedBalanceRow | null>(
    null
  );
  const [detailPage, setDetailPage] = React.useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = React.useState(5);

  const availableStockStates = React.useMemo(() => {
    const uniqueStates = new Set<StockState>();
    groupedData.forEach((group) => {
      group.rows.forEach((row) => {
        if (row.stock_state !== 'recycled') {
          uniqueStates.add(row.stock_state);
        }
      });
    });
    return ['all', ...Array.from(uniqueStates)];
  }, [groupedData]);

  React.useEffect(() => {
    if (!availableStockStates.includes(stockStateFilter)) {
      setStockStateFilter('all');
    }
  }, [availableStockStates, stockStateFilter]);

  const filteredData = React.useMemo<GroupedBalanceRow[]>(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    return groupedData.filter((group) => {
      const matchesState =
        stockStateFilter === 'all' ||
        group.rows.some((row) => row.stock_state === stockStateFilter);

      if (!matchesState) {
        return false;
      }

      if (!normalizedTerm) {
        return true;
      }

      const candidates = [
        group.product?.name,
        group.product?.description,
        group.product?.barcode,
        group.productId,
      ].filter(
        (candidate): candidate is string =>
          typeof candidate === 'string' && candidate.trim() !== ''
      );

      return candidates.some((candidate) =>
        String(candidate).toLowerCase().includes(normalizedTerm)
      );
    });
  }, [groupedData, searchTerm, stockStateFilter]);

  const detailRows = detailGroup?.rows ?? [];

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredData.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredData.length, rowsPerPage, page]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(detailRows.length / detailRowsPerPage) - 1
    );
    if (detailPage > maxPage) {
      setDetailPage(maxPage);
    }
  }, [detailRows.length, detailRowsPerPage, detailPage]);

  const paginatedData = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const buildStateSummaries = React.useCallback(
    (group: GroupedBalanceRow): StateSummary[] => {
      const summaries: StateSummary[] = [];

      STOCK_STATES_ORDER.forEach((state) => {
        if (state === 'corrected') {
          const positive = group.totalsByState.corrected_positive ?? 0;
          const negative = group.totalsByState.corrected_negative ?? 0;

          if (positive > 0) {
            summaries.push({
              key: 'corrected_positive',
              label:
                fr.correctedPositive ?? `${STOCK_STATE_LABEL.corrected} (+)`,
              quantity: positive,
            });
          }

          if (negative > 0) {
            summaries.push({
              key: 'corrected_negative',
              label:
                fr.correctedNegative ?? `${STOCK_STATE_LABEL.corrected} (-)`,
              quantity: negative,
            });
          }
        } else {
          const quantity = group.totalsByState[state] ?? 0;
          if (quantity > 0) {
            summaries.push({
              key: state,
              label: STOCK_STATE_LABEL[state],
              quantity,
            });
          }
        }
      });

      return summaries;
    },
    []
  );

  const handleOpenDetails = (group: GroupedBalanceRow) => {
    setDetailGroup(group);
    setDetailPage(0);
  };

  const handleCloseDetails = () => {
    setDetailGroup(null);
  };

  const paginatedDetailRows = React.useMemo(() => {
    const start = detailPage * detailRowsPerPage;
    const end = start + detailRowsPerPage;
    return detailRows.slice(start, end);
  }, [detailRows, detailPage, detailRowsPerPage]);

  const handleDetailPageChange = (
    _event: unknown,
    newPage: number
  ) => {
    setDetailPage(newPage);
  };

  const handleDetailRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDetailRowsPerPage(parseInt(event.target.value, 10));
    setDetailPage(0);
  };

  const detailProductName = detailGroup
    ? detailGroup.product?.name ??
      detailGroup.product?.description ??
      detailGroup.productId ??
      fr.product
    : '';

  const detailStoreName = detailGroup
    ? storeMap.get(detailGroup.rows[0]?.store_id ?? '') ??
      detailGroup.rows[0]?.store_id ??
      ''
    : '';

  const detailSummaries = detailGroup ? buildStateSummaries(detailGroup) : [];

  const detailLastMovement = detailGroup?.lastMovementTs
    ? formatDate(detailGroup.lastMovementTs)
    : '';

  if (isPending)
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  if (error)
    return <Alert severity="error">{(error as Error).message || fr.error}</Alert>;
  if (!groupedData.length)
    return <Alert severity="info">{fr.noResults}</Alert>;

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 3,
          background: (theme) =>
            `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.08)}, ${alpha(
              theme.palette.primary.main,
              0.04
            )})`,
        }}
      >
        <Typography variant="h6" color="primary" gutterBottom>
          {fr.balanceTitle}
        </Typography>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            background: (theme) =>
              `linear-gradient(120deg, ${alpha(theme.palette.success.light, 0.25)}, ${alpha(
                theme.palette.success.main,
                0.1
              )})`,
            border: (theme) => `1px solid ${alpha(theme.palette.success.dark, 0.15)}`,
            mb: 2,
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {fr.availableStockLabel}
            </Typography>
            <Typography variant="h4" color="success.main">
              {formattedAvailableTotal}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fr.availableStockDescription(
                selectedStoreName || fr.availableStockFallbackStore
              )}
            </Typography>
          </CardContent>
        </Card>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2 }}
        >
          <TextField
            fullWidth
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(0);
            }}
            placeholder={fr.searchBalancePlaceholder ?? fr.search}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label={fr.stockState}
            value={stockStateFilter}
            onChange={(event) => {
              setStockStateFilter(event.target.value as StockState | 'all');
              setPage(0);
            }}
            sx={{ minWidth: { md: 220 } }}
          >
            {availableStockStates.map((state) => (
              <MenuItem key={state} value={state}>
                {state === 'all'
                  ? 'Tous les états'
                  : STOCK_STATE_LABEL[state as keyof typeof STOCK_STATE_LABEL] ||
                    state ||
                    '—'}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TableContainer>
          <Table aria-label="balance-table">
            <TableHead>
              <TableRow>
                <TableCell>{fr.image}</TableCell>
                <TableCell>{fr.product}</TableCell>
                <TableCell>{fr.store}</TableCell>
                <TableCell>{fr.storeAvailability}</TableCell>
                <TableCell>{fr.stockStateSummary}</TableCell>
                <TableCell align="right">{fr.totalQuantity}</TableCell>
                <TableCell>{fr.lastMovement}</TableCell>
                <TableCell>{fr.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {fr.noResults}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((group) => {
                  const displayName =
                    group.product?.name ??
                    group.product?.description ??
                    group.productId ??
                    fr.product;
                  const storeName = group.rows.length
                    ? storeMap.get(group.rows[0].store_id) ?? group.rows[0].store_id
                    : '';
                  const printableProduct: ProductSelectionItem | null =
                    group.productId
                      ? group.product ?? {
                          id: group.productId,
                          name: displayName,
                        }
                      : null;
                  const isSelected =
                    !!group.productId && selectedProductId === group.productId;
                  const stateSummaries = buildStateSummaries(group);
                  const productImage = group.product?.imageUrl;
                  const availableForSelectedStore = group.rows.reduce(
                    (sum, row) =>
                      row.store_id === storeId && row.stock_state === 'available'
                        ? sum + row.quantity
                        : sum,
                    0
                  );
                  const isInSelectedStore = group.rows.some(
                    (row) => row.store_id === storeId
                  );

                  return (
                    <TableRow key={group.key} hover selected={isSelected}>
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={productImage}
                          alt={displayName}
                          sx={{ width: 48, height: 48 }}
                        >
                          {displayName?.[0] ?? '?'}
                        </Avatar>
                      </TableCell>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>{storeName}</TableCell>
                      <TableCell>
                        {isInSelectedStore ? (
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              borderColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.35),
                            }}
                          >
                            <CardContent sx={{ py: 1.5, px: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {fr.availableStockPerStore(
                                  selectedStoreName ||
                                    fr.availableStockFallbackStore
                                )}
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {availableForSelectedStore.toLocaleString(
                                  'fr-FR'
                                )}
                              </Typography>
                            </CardContent>
                          </Card>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {fr.outsideSelectedStore}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {stateSummaries.map((summary) => (
                            <Typography
                              key={summary.key}
                              variant="body2"
                              color="text.secondary"
                            >
                              {summary.label}:{' '}
                              <Box component="span" fontWeight={600}>
                                {summary.quantity}
                              </Box>
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{group.totalQuantity}</TableCell>
                      <TableCell>{
                        group.lastMovementTs ? formatDate(group.lastMovementTs) : '—'
                      }</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            startIcon={<InfoOutlinedIcon fontSize="small" />}
                            onClick={() => handleOpenDetails(group)}
                          >
                            {fr.viewDetails}
                          </Button>
                          {onPrintProduct && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<PrintIcon fontSize="small" />}
                              sx={{ borderRadius: 2 }}
                              disabled={!printableProduct}
                              onClick={() => {
                                if (printableProduct) {
                                  onPrintProduct(printableProduct);
                                }
                              }}
                            >
                              {fr.printBarcode}
                            </Button>
                          )}
                          {onSelectProduct && (
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              sx={{ borderRadius: 2, boxShadow: 'none' }}
                              onClick={() =>
                                printableProduct && onSelectProduct(printableProduct)
                              }
                              disabled={!printableProduct}
                            >
                              {fr.useProduct}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={fr.productsPerPage}
          labelDisplayedRows={formatPaginationLabel}
        />
      </Paper>

      <Dialog
        open={Boolean(detailGroup)}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{fr.detailsTitle(detailProductName)}</DialogTitle>
        <DialogContent dividers>
          {detailGroup && (
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                {detailStoreName && (
                  <Typography variant="body2" color="text.secondary">
                    {detailStoreName}
                  </Typography>
                )}
                {detailLastMovement && (
                  <Typography variant="body2" color="text.secondary">
                    {fr.lastMovement}: {detailLastMovement}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {fr.stockStateSummary}
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {detailSummaries.map((summary) => (
                    <Paper
                      key={summary.key}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        minWidth: 180,
                        flex: '1 1 160px',
                        background: (theme) =>
                          `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.light,
                            0.15
                          )}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        {summary.label}
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {summary.quantity}
                      </Typography>
                    </Paper>
                  ))}
                  <Paper
                    key="total"
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      minWidth: 180,
                      flex: '1 1 160px',
                      background: (theme) =>
                        `linear-gradient(135deg, ${alpha(
                          theme.palette.success.light,
                          0.2
                        )}, ${alpha(theme.palette.success.main, 0.08)})`,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {fr.totalQuantity}
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {detailGroup.totalQuantity}
                    </Typography>
                  </Paper>
                </Stack>
              </Stack>
              <Paper elevation={1} sx={{ borderRadius: 2 }}>
                <TableContainer>
                  <Table size="small" aria-label="balance-details-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{fr.stockState}</TableCell>
                        <TableCell align="right">{fr.quantity}</TableCell>
                        <TableCell>{fr.lastMovement}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDetailRows.map((row, index) => {
                        const isCorrected = row.stock_state === 'corrected';
                        const label = isCorrected
                          ? row.quantity >= 0
                            ? fr.correctedPositive ??
                              `${STOCK_STATE_LABEL.corrected} (+)`
                            : fr.correctedNegative ??
                              `${STOCK_STATE_LABEL.corrected} (-)`
                          : STOCK_STATE_LABEL[row.stock_state];
                        const quantity = isCorrected
                          ? Math.abs(row.quantity)
                          : row.quantity;

                        return (
                          <TableRow key={`${row.stock_state}-${index}`}>
                            <TableCell>{label}</TableCell>
                            <TableCell align="right">{quantity}</TableCell>
                            <TableCell>{formatDate(row.last_movement_ts)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={detailRows.length}
                  page={detailPage}
                  onPageChange={handleDetailPageChange}
                  rowsPerPage={detailRowsPerPage}
                  onRowsPerPageChange={handleDetailRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage={fr.productsPerPage}
                  labelDisplayedRows={formatPaginationLabel}
                />
              </Paper>
              <HistoryTable
                productId={detailGroup.productId ?? undefined}
                productName={detailProductName}
                storeId={detailGroup.rows[0]?.store_id ?? undefined}
                storeName={detailStoreName}
                storeNamesById={storeRecord}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseDetails}>
            {fr.close}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BalanceTable;
